// HerbalSoapAbout.js - Herbal soap themed About section component
import { DEVICES } from '../../../constants/breakpoints';

/**
 * Creates a herbal soap themed About section with brand story and mission
 * @param {function} getHierarchyName - Function to get hierarchy name for elements
 * @param {string} currentPageId - The current page ID where the about section is being added
 * @param {function} setClassStylesRegistry - Function to update class styles registry
 * @param {function} setGlobalClassNames - Function to update global class names
 * @returns {Object} - The about elements
 */
export const createHerbalSoapAboutElements = (
  getHierarchyName, 
  currentPageId,
  setClassStylesRegistry,
  setGlobalClassNames
) => {
  // Define class names
  const aboutSectionClass = 'herbal-soap-about-section';
  const aboutContainerClass = 'herbal-soap-about-container';
  const aboutContentClass = 'herbal-soap-about-content';
  const aboutImageWrapperClass = 'herbal-soap-about-image-wrapper';
  const aboutImageClass = 'herbal-soap-about-image';
  const aboutHeaderClass = 'herbal-soap-about-header';
  const aboutTitleClass = 'herbal-soap-about-title';
  const aboutSubtitleClass = 'herbal-soap-about-subtitle';
  const aboutTextClass = 'herbal-soap-about-text';
  const aboutFeaturesClass = 'herbal-soap-about-features';
  const aboutFeatureClass = 'herbal-soap-about-feature';
  const aboutFeatureIconClass = 'herbal-soap-about-feature-icon';
  const aboutFeatureTitleClass = 'herbal-soap-about-feature-title';
  const aboutFeatureDescClass = 'herbal-soap-about-feature-desc';
  const aboutStatsClass = 'herbal-soap-about-stats';
  const aboutStatClass = 'herbal-soap-about-stat';
  const aboutStatNumberClass = 'herbal-soap-about-stat-number';
  const aboutStatLabelClass = 'herbal-soap-about-stat-label';
  const aboutButtonClass = 'herbal-soap-about-button';
  const aboutButtonWrapperClass = 'herbal-soap-about-button-wrapper';

  // Desktop Styles - Herbal theme with natural green colors
  const aboutSectionStyles = {
    padding: '120px 20px',
    backgroundColor: '#ffffff',
    background: `
      linear-gradient(135deg, #ffffff 0%, #f8fffe 50%, #f0fdf4 100%),
      radial-gradient(circle at 80% 20%, rgba(144, 238, 144, 0.03) 0%, transparent 50%),
      radial-gradient(circle at 20% 80%, rgba(34, 139, 34, 0.02) 0%, transparent 50%)
    `,
    position: 'relative',
    overflow: 'hidden'
  };

  const aboutContainerStyles = {
    maxWidth: '1400px',
    margin: '0 auto',
    width: '100%',
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '80px',
    alignItems: 'center'
  };

  const aboutContentStyles = {
    display: 'flex',
    flexDirection: 'column',
    gap: '32px'
  };

  const aboutHeaderStyles = {
    marginBottom: '24px'
  };

  const aboutTitleStyles = {
    fontSize: '48px',
    fontWeight: '700',
    color: '#1a3d1a',
    marginBottom: '16px',
    fontFamily: '"Inter", sans-serif',
    lineHeight: '1.2'
  };

  const aboutSubtitleStyles = {
    fontSize: '20px',
    color: '#228b22',
    fontWeight: '600',
    textTransform: 'uppercase',
    letterSpacing: '1px',
    marginBottom: '8px',
    fontFamily: '"Inter", sans-serif'
  };

  const aboutTextStyles = {
    fontSize: '18px',
    color: '#4a5568',
    lineHeight: '1.7',
    marginBottom: '32px',
    fontFamily: '"Inter", sans-serif'
  };

  const aboutFeaturesStyles = {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, 1fr)',
    gap: '24px',
    marginBottom: '40px'
  };

  const aboutFeatureStyles = {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '16px',
    padding: '20px',
    backgroundColor: 'rgba(144, 238, 144, 0.05)',
    borderRadius: '12px',
    border: '1px solid rgba(144, 238, 144, 0.1)',
    transition: 'all 0.3s ease'
  };

  const aboutFeatureHoverStyles = {
    backgroundColor: 'rgba(144, 238, 144, 0.08)',
    borderColor: 'rgba(144, 238, 144, 0.2)',
    transform: 'translateY(-2px)'
  };

  const aboutFeatureIconStyles = {
    width: '48px',
    height: '48px',
    backgroundColor: '#90EE90',
    borderRadius: '12px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '24px',
    color: '#1a3d1a',
    flexShrink: '0'
  };

  const aboutFeatureTitleStyles = {
    fontSize: '16px',
    fontWeight: '600',
    color: '#1a3d1a',
    marginBottom: '8px',
    fontFamily: '"Inter", sans-serif'
  };

  const aboutFeatureDescStyles = {
    fontSize: '14px',
    color: '#6b7280',
    lineHeight: '1.5',
    fontFamily: '"Inter", sans-serif'
  };

  const aboutStatsStyles = {
    display: 'flex',
    gap: '32px',
    marginBottom: '40px'
  };

  const aboutStatStyles = {
    textAlign: 'center'
  };

  const aboutStatNumberStyles = {
    fontSize: '36px',
    fontWeight: '700',
    color: '#228b22',
    marginBottom: '8px',
    fontFamily: '"Inter", sans-serif'
  };

  const aboutStatLabelStyles = {
    fontSize: '14px',
    color: '#6b7280',
    fontWeight: '500',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    fontFamily: '"Inter", sans-serif'
  };

  const aboutButtonWrapperStyles = {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    width: '100%'
  };

  const aboutButtonStyles = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '8px',
    padding: '16px 32px',
    backgroundColor: '#228b22',
    background: `linear-gradient(135deg, #228b22 0%, #90EE90 100%)`,
    color: '#ffffff',
    border: 'none',
    borderRadius: '8px',
    fontSize: '16px',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'all 0.3s ease',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    fontFamily: '"Inter", sans-serif',
    textDecoration: 'none'
  };

  const aboutButtonHoverStyles = {
    background: `linear-gradient(135deg, #1a3d1a 0%, #228b22 100%)`,
    transform: 'translateY(-2px)',
    boxShadow: '0 6px 20px rgba(34, 139, 34, 0.3)'
  };

  const aboutImageWrapperStyles = {
    position: 'relative',
    borderRadius: '16px',
    overflow: 'hidden',
    boxShadow: '0 20px 40px rgba(0, 0, 0, 0.1)',
    border: '3px solid rgba(144, 238, 144, 0.2)'
  };

  const aboutImageStyles = {
    width: '100%',
    height: '600px',
    objectFit: 'cover',
    transition: 'all 0.4s ease'
  };

  const aboutImageHoverStyles = {
    transform: 'scale(1.02)'
  };

  // Tablet Styles
  const tabletAboutContainerStyles = {
    ...aboutContainerStyles,
    gridTemplateColumns: '1fr',
    gap: '60px',
    textAlign: 'center'
  };

  const tabletAboutTitleStyles = {
    ...aboutTitleStyles,
    fontSize: '40px'
  };

  const tabletAboutFeaturesStyles = {
    ...aboutFeaturesStyles,
    gridTemplateColumns: 'repeat(2, 1fr)',
    gap: '20px'
  };

  const tabletAboutStatsStyles = {
    ...aboutStatsStyles,
    justifyContent: 'center'
  };

  // Mobile Styles
  const mobileAboutSectionStyles = {
    ...aboutSectionStyles,
    padding: '80px 20px'
  };

  const mobileAboutContainerStyles = {
    ...aboutContainerStyles,
    gridTemplateColumns: '1fr',
    gap: '50px',
    textAlign: 'center'
  };

  const mobileAboutTitleStyles = {
    ...aboutTitleStyles,
    fontSize: '32px'
  };

  const mobileAboutTextStyles = {
    ...aboutTextStyles,
    fontSize: '16px'
  };

  const mobileAboutFeaturesStyles = {
    ...aboutFeaturesStyles,
    gridTemplateColumns: '1fr',
    gap: '16px'
  };

  const mobileAboutStatsStyles = {
    ...aboutStatsStyles,
    flexDirection: 'column',
    gap: '24px'
  };

  const mobileAboutImageWrapperStyles = {
    ...aboutImageWrapperStyles,
    order: '-1'
  };

  const mobileAboutImageStyles = {
    ...aboutImageStyles,
    height: '400px'
  };

  // Update the classStylesRegistry
  setClassStylesRegistry(prev => {
    const newRegistry = { ...prev };
    
    // Desktop styles
    newRegistry[DEVICES.DESKTOP].set(aboutSectionClass, aboutSectionStyles);
    newRegistry[DEVICES.DESKTOP].set(aboutContainerClass, aboutContainerStyles);
    newRegistry[DEVICES.DESKTOP].set(aboutContentClass, aboutContentStyles);
    newRegistry[DEVICES.DESKTOP].set(aboutHeaderClass, aboutHeaderStyles);
    newRegistry[DEVICES.DESKTOP].set(aboutTitleClass, aboutTitleStyles);
    newRegistry[DEVICES.DESKTOP].set(aboutSubtitleClass, aboutSubtitleStyles);
    newRegistry[DEVICES.DESKTOP].set(aboutTextClass, aboutTextStyles);
    newRegistry[DEVICES.DESKTOP].set(aboutFeaturesClass, aboutFeaturesStyles);
    newRegistry[DEVICES.DESKTOP].set(aboutFeatureClass, aboutFeatureStyles);
    newRegistry[DEVICES.DESKTOP].set(aboutFeatureIconClass, aboutFeatureIconStyles);
    newRegistry[DEVICES.DESKTOP].set(aboutFeatureTitleClass, aboutFeatureTitleStyles);
    newRegistry[DEVICES.DESKTOP].set(aboutFeatureDescClass, aboutFeatureDescStyles);
    newRegistry[DEVICES.DESKTOP].set(aboutStatsClass, aboutStatsStyles);
    newRegistry[DEVICES.DESKTOP].set(aboutStatClass, aboutStatStyles);
    newRegistry[DEVICES.DESKTOP].set(aboutStatNumberClass, aboutStatNumberStyles);
    newRegistry[DEVICES.DESKTOP].set(aboutStatLabelClass, aboutStatLabelStyles);
    newRegistry[DEVICES.DESKTOP].set(aboutButtonWrapperClass, aboutButtonWrapperStyles);
    newRegistry[DEVICES.DESKTOP].set(aboutButtonClass, aboutButtonStyles);
    newRegistry[DEVICES.DESKTOP].set(aboutImageWrapperClass, aboutImageWrapperStyles);
    newRegistry[DEVICES.DESKTOP].set(aboutImageClass, aboutImageStyles);

    // Hover styles
    newRegistry[DEVICES.DESKTOP].set(`${aboutFeatureClass}--hover`, aboutFeatureHoverStyles);
    newRegistry[DEVICES.DESKTOP].set(`${aboutButtonClass}--hover`, aboutButtonHoverStyles);
    newRegistry[DEVICES.DESKTOP].set(`${aboutImageClass}--hover`, aboutImageHoverStyles);

    // Tablet styles
    newRegistry[DEVICES.TABLET].set(aboutContainerClass, tabletAboutContainerStyles);
    newRegistry[DEVICES.TABLET].set(aboutTitleClass, tabletAboutTitleStyles);
    newRegistry[DEVICES.TABLET].set(aboutFeaturesClass, tabletAboutFeaturesStyles);
    newRegistry[DEVICES.TABLET].set(aboutStatsClass, tabletAboutStatsStyles);

    // Mobile styles
    newRegistry[DEVICES.MOBILE].set(aboutSectionClass, mobileAboutSectionStyles);
    newRegistry[DEVICES.MOBILE].set(aboutContainerClass, mobileAboutContainerStyles);
    newRegistry[DEVICES.MOBILE].set(aboutTitleClass, mobileAboutTitleStyles);
    newRegistry[DEVICES.MOBILE].set(aboutTextClass, mobileAboutTextStyles);
    newRegistry[DEVICES.MOBILE].set(aboutFeaturesClass, mobileAboutFeaturesStyles);
    newRegistry[DEVICES.MOBILE].set(aboutStatsClass, mobileAboutStatsStyles);
    newRegistry[DEVICES.MOBILE].set(aboutImageWrapperClass, mobileAboutImageWrapperStyles);
    newRegistry[DEVICES.MOBILE].set(aboutImageClass, mobileAboutImageStyles);

    return newRegistry;
  });

  // Add classes to globalClassNames
  setGlobalClassNames(prev => new Set([
    ...prev,
    aboutSectionClass,
    aboutContainerClass,
    aboutContentClass,
    aboutHeaderClass,
    aboutTitleClass,
    aboutSubtitleClass,
    aboutTextClass,
    aboutFeaturesClass,
    aboutFeatureClass,
    aboutFeatureIconClass,
    aboutFeatureTitleClass,
    aboutFeatureDescClass,
    aboutStatsClass,
    aboutStatClass,
    aboutStatNumberClass,
    aboutStatLabelClass,
    aboutButtonClass,
    aboutImageWrapperClass,
    aboutImageClass,
    aboutButtonWrapperClass
  ]));

  // Generate IDs for all elements
  const aboutSectionId = Date.now();
  const aboutContainerId = aboutSectionId + 1;
  const aboutContentId = aboutSectionId + 2;
  const aboutHeaderId = aboutSectionId + 3;
  const aboutSubtitleId = aboutSectionId + 4;
  const aboutTitleId = aboutSectionId + 5;
  const aboutTextId = aboutSectionId + 6;
  const aboutFeaturesId = aboutSectionId + 7;
  const aboutStatsId = aboutSectionId + 8;
  const aboutButtonWrapperId = aboutSectionId + 9;
  const aboutButtonId = aboutSectionId + 10;
  const aboutImageWrapperId = aboutSectionId + 11;
  const aboutImageId = aboutSectionId + 12;

  // Features data
  const features = [
    {
      icon: '🌿',
      title: '100% Natural',
      description: 'Made with pure organic herbs and essential oils, free from harmful chemicals.'
    },
    {
      icon: '🤲',
      title: 'Handcrafted',
      description: 'Each soap bar is carefully handmade using traditional artisanal methods.'
    },
    {
      icon: '🌱',
      title: 'Eco-Friendly',
      description: 'Sustainable packaging and environmentally conscious production process.'
    },
    {
      icon: '✨',
      title: 'Skin Nourishing',
      description: 'Enriched with vitamins and minerals that naturally moisturize and heal.'
    }
  ];

  // Stats data
  const stats = [
    { number: '10+', label: 'Years Experience' },
    { number: '5000+', label: 'Happy Customers' },
    { number: '50+', label: 'Natural Ingredients' }
  ];

  // Create feature elements
  let currentId = aboutImageId + 1;
  const featureElements = [];

  features.forEach((feature, index) => {
    const featureId = currentId++;
    const featureIconId = currentId++;
    const featureContentId = currentId++;
    const featureTitleId = currentId++;
    const featureDescId = currentId++;

    featureElements.push(
      // Feature Container
      {
        id: featureId,
        builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        type: 'div',
        name: `${feature.title} Feature`,
        parentId: aboutFeaturesId,
        hierarchyName: `${feature.title} Feature`,
        className: aboutFeatureClass,
        styles: {},
        children: [featureIconId, featureContentId],
        text: '',
        visibility: 'visible',
        isExpanded: true,
        attributes: {
          'data-element': 'herbal-soap-about-feature'
        }
      },
      // Feature Icon
      {
        id: featureIconId,
        builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        type: 'div',
        name: `${feature.title} Icon`,
        parentId: featureId,
        hierarchyName: `${feature.title} Icon`,
        className: aboutFeatureIconClass,
        styles: {},
        children: [],
        text: feature.icon,
        visibility: 'visible',
        isExpanded: true,
        attributes: {
          'data-element': 'herbal-soap-about-feature-icon'
        }
      },
      // Feature Content
      {
        id: featureContentId,
        builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        type: 'div',
        name: `${feature.title} Content`,
        parentId: featureId,
        hierarchyName: `${feature.title} Content`,
        className: '',
        styles: {},
        children: [featureTitleId, featureDescId],
        text: '',
        visibility: 'visible',
        isExpanded: true,
        attributes: {
          'data-element': 'herbal-soap-about-feature-content'
        }
      },
      // Feature Title
      {
        id: featureTitleId,
        builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        type: 'h4',
        name: `${feature.title} Title`,
        parentId: featureContentId,
        hierarchyName: `${feature.title} Title`,
        className: aboutFeatureTitleClass,
        styles: {},
        children: [],
        text: feature.title,
        visibility: 'visible',
        isExpanded: true,
        attributes: {
          'data-element': 'herbal-soap-about-feature-title'
        }
      },
      // Feature Description
      {
        id: featureDescId,
        builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        type: 'p',
        name: `${feature.title} Description`,
        parentId: featureContentId,
        hierarchyName: `${feature.title} Description`,
        className: aboutFeatureDescClass,
        styles: {},
        children: [],
        text: feature.description,
        visibility: 'visible',
        isExpanded: true,
        attributes: {
          'data-element': 'herbal-soap-about-feature-desc'
        }
      }
    );
  });

  // Create stats elements
  const statsElements = [];
  stats.forEach((stat, index) => {
    const statId = currentId++;
    const statNumberId = currentId++;
    const statLabelId = currentId++;

    statsElements.push(
      // Stat Container
      {
        id: statId,
        builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        type: 'div',
        name: `${stat.label} Stat`,
        parentId: aboutStatsId,
        hierarchyName: `${stat.label} Stat`,
        className: aboutStatClass,
        styles: {},
        children: [statNumberId, statLabelId],
        text: '',
        visibility: 'visible',
        isExpanded: true,
        attributes: {
          'data-element': 'herbal-soap-about-stat'
        }
      },
      // Stat Number
      {
        id: statNumberId,
        builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        type: 'div',
        name: `${stat.label} Number`,
        parentId: statId,
        hierarchyName: `${stat.label} Number`,
        className: aboutStatNumberClass,
        styles: {},
        children: [],
        text: stat.number,
        visibility: 'visible',
        isExpanded: true,
        attributes: {
          'data-element': 'herbal-soap-about-stat-number'
        }
      },
      // Stat Label
      {
        id: statLabelId,
        builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        type: 'div',
        name: `${stat.label} Label`,
        parentId: statId,
        hierarchyName: `${stat.label} Label`,
        className: aboutStatLabelClass,
        styles: {},
        children: [],
        text: stat.label,
        visibility: 'visible',
        isExpanded: true,
        attributes: {
          'data-element': 'herbal-soap-about-stat-label'
        }
      }
    );
  });

  // Get feature and stats IDs for containers
  const featureIds = featureElements
    .filter(element => element.parentId === aboutFeaturesId)
    .map(element => element.id);

  const statsIds = statsElements
    .filter(element => element.parentId === aboutStatsId)
    .map(element => element.id);

  // Create base elements
  const baseElements = [
    // Main About Section
    {
      id: aboutSectionId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'section',
      name: 'Herbal Soap About Section',
      parentId: null,
      hierarchyName: 'Herbal Soap About Section',
      className: aboutSectionClass,
      styles: {},
      children: [aboutContainerId],
      text: '',
      visibility: 'visible',
      isExpanded: true,
      attributes: {
        'data-element': 'herbal-soap-about-section',
        'data-component': 'section',
        'data-section-type': 'about',
        'data-page-id': currentPageId
      }
    },

    // Container
    {
      id: aboutContainerId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'About Container',
      parentId: aboutSectionId,
      hierarchyName: 'About Container',
      className: aboutContainerClass,
      styles: {},
      children: [aboutContentId, aboutImageWrapperId],
      text: '',
      visibility: 'visible',
      isExpanded: true,
      attributes: {
        'data-element': 'herbal-soap-about-container'
      }
    },

    // Content Area
    {
      id: aboutContentId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'About Content',
      parentId: aboutContainerId,
      hierarchyName: 'About Content',
      className: aboutContentClass,
      styles: {},
      children: [aboutHeaderId, aboutTextId, aboutFeaturesId, aboutStatsId, aboutButtonWrapperId],
      text: '',
      visibility: 'visible',
      isExpanded: true,
      attributes: {
        'data-element': 'herbal-soap-about-content'
      }
    },

    // Header
    {
      id: aboutHeaderId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'About Header',
      parentId: aboutContentId,
      hierarchyName: 'About Header',
      className: aboutHeaderClass,
      styles: {},
      children: [aboutSubtitleId, aboutTitleId],
      text: '',
      visibility: 'visible',
      isExpanded: true,
      attributes: {
        'data-element': 'herbal-soap-about-header'
      }
    },

    // Subtitle
    {
      id: aboutSubtitleId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'span',
      name: 'About Subtitle',
      parentId: aboutHeaderId,
      hierarchyName: 'About Subtitle',
      className: aboutSubtitleClass,
      styles: {},
      children: [],
      text: 'Our Story',
      visibility: 'visible',
      isExpanded: true,
      attributes: {
        'data-element': 'herbal-soap-about-subtitle'
      }
    },

    // Title
    {
      id: aboutTitleId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'h2',
      name: 'About Title',
      parentId: aboutHeaderId,
      hierarchyName: 'About Title',
      className: aboutTitleClass,
      styles: {},
      children: [],
      text: 'Crafting Nature\'s Goodness for Your Skin',
      visibility: 'visible',
      isExpanded: true,
      attributes: {
        'data-element': 'herbal-soap-about-title'
      }
    },

    // About Text
    {
      id: aboutTextId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'p',
      name: 'About Text',
      parentId: aboutContentId,
      hierarchyName: 'About Text',
      className: aboutTextClass,
      styles: {},
      children: [],
      text: 'Founded with a passion for natural wellness, we believe that the best skincare comes from nature itself. Our journey began in a small kitchen, experimenting with traditional herbal recipes passed down through generations. Today, we continue to handcraft each soap bar using only the finest organic ingredients, ensuring that every product delivers the pure, healing power of nature to your daily routine.',
      visibility: 'visible',
      isExpanded: true,
      attributes: {
        'data-element': 'herbal-soap-about-text'
      }
    },

    // Features Container
    {
      id: aboutFeaturesId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'About Features',
      parentId: aboutContentId,
      hierarchyName: 'About Features',
      className: aboutFeaturesClass,
      styles: {},
      children: featureIds,
      text: '',
      visibility: 'visible',
      isExpanded: true,
      attributes: {
        'data-element': 'herbal-soap-about-features'
      }
    },

    // Stats Container
    {
      id: aboutStatsId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'About Stats',
      parentId: aboutContentId,
      hierarchyName: 'About Stats',
      className: aboutStatsClass,
      styles: {},
      children: statsIds,
      text: '',
      visibility: 'visible',
      isExpanded: true,
      attributes: {
        'data-element': 'herbal-soap-about-stats'
      }
    },

    // CTA Button Wrapper
    {
      id: aboutButtonWrapperId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'About CTA Button Wrapper',
      parentId: aboutContentId,
      hierarchyName: 'About CTA Button Wrapper',
      className: aboutButtonWrapperClass,
      styles: {},
      children: [aboutButtonId],
      text: '',
      visibility: 'visible',
      isExpanded: true,
      attributes: {
        'data-element': 'herbal-soap-about-button-wrapper'
      }
    },

    // CTA Button
    {
      id: aboutButtonId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'link',
      name: 'About CTA Button',
      parentId: aboutButtonWrapperId,
      hierarchyName: 'About CTA Button',
      className: aboutButtonClass,
      styles: {},
      children: [],
      text: 'Shop Our Products',
      visibility: 'visible',
      isExpanded: true,
      linkTo: 'shop',
      attributes: {
        'data-element': 'herbal-soap-about-button'
      }
    },

    // Image Wrapper
    {
      id: aboutImageWrapperId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'About Image Wrapper',
      parentId: aboutContainerId,
      hierarchyName: 'About Image Wrapper',
      className: aboutImageWrapperClass,
      styles: {},
      children: [aboutImageId],
      text: '',
      visibility: 'visible',
      isExpanded: true,
      attributes: {
        'data-element': 'herbal-soap-about-image-wrapper'
      }
    },

    // About Image
    {
      id: aboutImageId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'image',
      name: 'About Image',
      parentId: aboutImageWrapperId,
      hierarchyName: 'About Image',
      className: aboutImageClass,
      styles: {},
      children: [],
      text: '',
      visibility: 'visible',
      isExpanded: true,
      src: 'https://images.unsplash.com/photo-1556909114-f6e7ad7d3136?q=80&w=2187&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D',
      attributes: {
        'data-element': 'herbal-soap-about-image',
        'alt': 'Herbal soap making process with natural ingredients'
      }
    }
  ];

  // Combine all elements
  const allElements = [...baseElements, ...featureElements, ...statsElements];
  
  // Return the created elements
  return {
    elements: allElements
  };
};